import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { DisclaimerFooter } from "@/components/Bits";
import TradingViewChart from "@/components/TradingViewChart";
import DemonEye from "@/components/DemonEye";
import { CandlestickChart, Maximize2, X, GripVertical, Radio, Send } from "lucide-react";

const LOGO = "/tmc-logo.jpeg";

const SYMBOLS = [
  { label: "XAU/USD", v: "OANDA:XAUUSD" },
  { label: "BTC/USD", v: "BINANCE:BTCUSDT" },
];

// Results only from GOLD (XAU/USD) and BITCOIN (BTC/USD) — mostly wins, a few losses.
const RESULTS = [
  { pair: "XAU/USD", pips: "+540" },
  { pair: "BTC/USD", pips: "+820" },
  { pair: "XAU/USD", pips: "+275" },
  { pair: "BTC/USD", pips: "+610" },
  { pair: "XAU/USD", pips: "+420" },
  { pair: "XAU/USD", pips: "-120" },
  { pair: "BTC/USD", pips: "+390" },
  { pair: "XAU/USD", pips: "+600" },
  { pair: "BTC/USD", pips: "+450" },
  { pair: "XAU/USD", pips: "+180" },
  { pair: "BTC/USD", pips: "-95" },
  { pair: "XAU/USD", pips: "+710" },
];

function PremiumCard({ children, className = "", glow = "violet" }) {
  const shadow = glow === "gold" ? "shadow-[0_0_34px_rgba(250,204,21,0.35)]" : "shadow-[0_0_34px_rgba(168,85,247,0.28)]";
  const grad = glow === "gold"
    ? "from-[#FACC15]/70 via-amber-500/30 to-violet-900/30"
    : "from-violet-500/60 via-fuchsia-500/25 to-violet-900/30";
  return (
    <div className={`rounded-2xl p-[1.5px] bg-gradient-to-br ${grad} ${shadow} ${className}`}>
      <div className="rounded-[15px] bg-[#120826]/95 backdrop-blur-xl h-full">{children}</div>
    </div>
  );
}

function CardHead({ title, accent = "violet" }) {
  const bar = accent === "gold" ? "from-[#FACC15] to-amber-500" : "from-violet-400 to-fuchsia-500";
  return (
    <div className="flex items-center gap-2 px-4 pt-3.5 pb-2 cursor-grab active:cursor-grabbing">
      <span className={`w-1 h-4 rounded-full bg-gradient-to-b ${bar}`} />
      <span className="text-[10px] tracking-[0.28em] text-violet-200 font-semibold">{title}</span>
      <GripVertical size={12} className="ml-auto text-violet-400/40" />
    </div>
  );
}

function PipTape() {
  const list = [...RESULTS, ...RESULTS];
  return (
    <div className="overflow-hidden relative h-full flex items-center" data-testid="pip-results-ticker">
      <motion.div
        className="flex gap-6 absolute whitespace-nowrap"
        animate={{ x: ["0%", "-50%"] }}
        transition={{ duration: 26, repeat: Infinity, ease: "linear" }}
      >
        {list.map((r, i) => (
          <span key={i} className="font-mono text-sm font-bold flex items-center gap-1.5">
            <span className={r.pair.startsWith("XAU") ? "text-[#FACC15]" : "text-orange-400"}>{r.pair}</span>
            <span className={r.pips.startsWith("-") ? "text-[#FF2A55]" : "text-[#00FF9D]"}>{r.pips} pips</span>
          </span>
        ))}
      </motion.div>
    </div>
  );
}

function ResultsBoard() {
  const wins = RESULTS.filter((r) => !r.pips.startsWith("-")).length;
  const wr = Math.round((wins / RESULTS.length) * 100);
  return (
    <div className="pb-3" data-testid="results-board">
      <CardHead title="WYNIKI · ZŁOTO & BITCOIN" />
      <div className="px-4 pb-2.5 flex items-center justify-between">
        <div>
          <div className="text-[9px] tracking-widest text-violet-300/70">WIN RATE</div>
          <div className="font-mono font-black text-[#00FF9D] text-xl leading-none">{wr}%</div>
        </div>
        <div className="text-right">
          <div className="text-[9px] tracking-widest text-violet-300/70">TRANSAKCJE</div>
          <div className="font-mono font-black text-white text-xl leading-none">{RESULTS.length}</div>
        </div>
      </div>
      <div className="px-3 space-y-1.5 max-h-64 overflow-y-auto no-scrollbar">
        {RESULTS.map((r, i) => {
          const win = !r.pips.startsWith("-");
          const gold = r.pair.startsWith("XAU");
          return (
            <div key={i} className="flex items-center gap-2.5 px-3 py-2 rounded-xl bg-black/40 border border-violet-500/15">
              <span className={`w-2 h-2 rounded-full ${gold ? "bg-[#FACC15]" : "bg-orange-400"}`} />
              <span className="font-mono text-xs font-bold text-white">{r.pair}</span>
              <span className={`ml-auto font-mono text-base font-black ${win ? "text-[#00FF9D]" : "text-[#FF2A55]"}`}>
                {r.pips} <span className="text-[10px] opacity-70">pips</span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TelegramPanel({ groupUrl, big = false }) {
  return (
    <PremiumCard glow="gold">
      <a
        href={groupUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="telegram-cta-panel"
        className="block p-5 text-center"
      >
        <div className="flex items-center justify-center gap-2 mb-1.5">
          <Send size={big ? 22 : 18} className="text-[#FACC15]" />
          <div className={`font-display font-black tracking-wide text-[#FACC15] ${big ? "text-lg" : "text-sm"}`}>
            DARMOWA GRUPA TELEGRAM
          </div>
        </div>
        <div className="text-[11px] text-violet-200/80 mb-3">Dołącz i odbieraj sygnały na żywo!</div>
        <div
          data-testid="group-link-text"
          className={`font-mono font-black break-all text-yellow-300 drop-shadow-[0_0_10px_rgba(250,204,21,0.6)] ${
            big ? "text-2xl" : "text-base"
          }`}
        >
          {groupUrl.replace(/^https?:\/\//, "")}
        </div>
      </a>
    </PremiumCard>
  );
}

function CornerLogo({ side, label }) {
  const pos = side === "left" ? "left-4" : "right-4";
  return (
    <div
      data-testid={`tmc-bottom-logo-${side}`}
      className={`fixed bottom-11 ${pos} z-[105] flex items-center gap-2.5 p-1.5 pr-3.5 bg-black/80 border border-violet-500/50 rounded-xl shadow-[0_0_18px_rgba(168,85,247,0.5)] backdrop-blur`}
    >
      <img src={LOGO} alt="TMC" className="w-10 h-10 rounded-lg object-cover" />
      <div className="leading-tight">
        <div className="font-display font-black text-xs text-white tracking-wide">THE MONEY CLUB</div>
        <div className="text-[9px] tracking-widest text-[#FACC15] font-bold">{label}</div>
      </div>
    </div>
  );
}

function StreamRibbon() {
  return (
    <div className="h-14 flex items-center gap-4 px-4 bg-[#070212] border-b border-violet-500/40" data-testid="stream-ribbon">
      <div className="flex items-center gap-3 shrink-0">
        <img src={LOGO} alt="TMC" className="w-9 h-9 rounded-lg object-cover ring-1 ring-violet-500/40" />
        <div className="hidden sm:block leading-tight">
          <div className="font-display font-black text-sm text-white">THE MONEY CLUB</div>
          <div className="text-[8px] tracking-[0.3em] text-violet-300">92 DAYS CHALLENGE</div>
        </div>
      </div>

      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FF2A55]/10 border border-[#FF2A55]/40 shrink-0">
        <motion.span className="w-2 h-2 rounded-full bg-[#FF2A55]" animate={{ opacity: [1, 0.3, 1] }} transition={{ duration: 1.4, repeat: Infinity }} />
        <span className="text-[10px] tracking-widest text-[#FF2A55] font-bold">LIVE</span>
      </div>

      <div className="flex-1 min-w-0 h-full border-l border-violet-500/20 pl-4">
        <PipTape />
      </div>
    </div>
  );
}

function Draggable({ children, initial, float = false, testid, className = "" }) {
  return (
    <motion.div
      data-testid={testid}
      drag
      dragMomentum={false}
      dragElastic={0}
      whileDrag={{ scale: 1.02, zIndex: 120 }}
      className={`fixed z-[110] ${className}`}
      style={{ ...initial }}
    >
      {float ? (
        <motion.div animate={{ y: [0, -8, 0] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}>
          {children}
        </motion.div>
      ) : (
        children
      )}
    </motion.div>
  );
}

export default function Terminal() {
  const [symbol, setSymbol] = useState("OANDA:XAUUSD");
  const [full, setFull] = useState(false);
  const [stream, setStream] = useState(false);
  const [groupUrl, setGroupUrl] = useState("https://t.me/DEMONCLUB92");
  const [studies, setStudies] = useState([]);

  useEffect(() => {
    api
      .get("/settings")
      .then(({ data }) => {
        if (data.telegram_group) setGroupUrl(data.telegram_group);
        if (Array.isArray(data.tv_studies)) setStudies(data.tv_studies);
      })
      .catch(() => {});
  }, []);

  const vh = typeof window !== "undefined" ? window.innerHeight : 800;

  const SymbolBar = () => (
    <div className="flex flex-wrap items-center gap-2">
      {SYMBOLS.map((s) => (
        <button
          key={s.v}
          data-testid={`symbol-${s.label}`}
          onClick={() => setSymbol(s.v)}
          className={`px-3 h-8 rounded-full text-xs font-mono border transition ${
            symbol === s.v
              ? "bg-violet-600/30 border-violet-500/60 text-white"
              : "border-violet-900/40 text-violet-300/70 hover:text-white"
          }`}
        >
          {s.label}
        </button>
      ))}
    </div>
  );

  // ---------------- STREAM MODE ----------------
  if (stream) {
    const chartH = vh - 56 - 32 - 24;
    return createPortal(
      <div className="fixed inset-0 z-[100] bg-[#0B031A] overflow-hidden flex flex-col" data-testid="stream-mode">
        <StreamRibbon />

        <div className="flex-1 min-h-0 p-3 relative">
          <div className="absolute top-1 left-4 right-4 flex items-center justify-between z-[108] pointer-events-none">
            <div className="pointer-events-auto">
              <SymbolBar />
            </div>
            <button
              data-testid="stream-mode-toggle-button"
              onClick={() => setStream(false)}
              className="pointer-events-auto inline-flex items-center gap-2 h-9 px-4 rounded-full bg-black/70 border border-violet-500/50 text-white text-xs font-display font-semibold hover:bg-violet-600/20"
            >
              <X size={15} /> WYJDŹ ZE STREAMU
            </button>
          </div>
          <div
            className="w-full h-full mt-11 rounded-xl border border-violet-500/40 overflow-hidden shadow-[0_0_40px_rgba(168,85,247,0.18)]"
            style={{ height: chartH }}
            data-testid="tradingview-chart-container"
          >
            <TradingViewChart key="tv-stream" symbol={symbol} height={chartH} studies={studies} />
          </div>
        </div>

        <Draggable testid="widget-telegram" initial={{ top: 104, right: 24 }} float className="w-[86vw] sm:w-[320px]">
          <TelegramPanel groupUrl={groupUrl} big />
        </Draggable>

        <Draggable testid="widget-results" initial={{ top: 300, right: 24 }} className="w-[86vw] sm:w-[300px]">
          <PremiumCard>
            <ResultsBoard />
          </PremiumCard>
        </Draggable>

        <CornerLogo side="left" label="TMC ACADEMY" />
        <CornerLogo side="right" label="GOAL: $200,000" />

        <div
          data-testid="disclaimer-banner"
          className="fixed bottom-0 left-0 right-0 h-8 bg-black/90 border-t border-[#FACC15]/50 flex items-center justify-center text-[#FCD34D] text-[11px] font-semibold px-4 z-[115] shadow-[0_-4px_15px_rgba(0,0,0,0.8)]"
        >
          ⚠️ Materiały mają charakter wyłącznie edukacyjny i nie stanowią porady ani rekomendacji inwestycyjnej.
        </div>
      </div>,
      document.body
    );
  }

  // ---------------- NORMAL MODE ----------------
  return (
    <div>
      <div className={full ? "fixed inset-0 z-[70] p-3 sm:p-4 bg-[#07060c] overflow-auto" : "glow-card rounded-2xl p-4 sm:p-5"}>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <CandlestickChart className="text-violet-400" size={20} />
            <h2 className="font-display font-semibold text-lg">TradingView</h2>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <SymbolBar />
            <button
              data-testid="stream-mode-enter-button"
              onClick={() => setStream(true)}
              className="inline-flex items-center gap-2 h-8 px-4 rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white text-xs font-display font-bold shadow-[0_0_16px_rgba(168,85,247,0.5)] hover:brightness-110"
            >
              <Radio size={14} /> TRYB STREAM
            </button>
            <button
              data-testid="tv-fullscreen"
              onClick={() => setFull((f) => !f)}
              className="w-8 h-8 rounded-full border border-violet-900/40 text-violet-300/70 hover:text-white flex items-center justify-center"
              title="Pełna szerokość"
            >
              {full ? <X size={15} /> : <Maximize2 size={15} />}
            </button>
          </div>
        </div>
        <TradingViewChart key={`tv-${full}`} symbol={symbol} height={full ? vh - 110 : 640} studies={studies} />
      </div>

      <Draggable testid="widget-results" initial={{ top: 92, right: 16 }} className="w-[90vw] sm:w-[300px]">
        <PremiumCard>
          <ResultsBoard />
        </PremiumCard>
      </Draggable>

      <Draggable testid="widget-group" initial={{ bottom: 24, left: 16 }} float className="w-[80vw] sm:w-[260px]">
        <TelegramPanel groupUrl={groupUrl} />
      </Draggable>

      {!full && <DisclaimerFooter />}
    </div>
  );
}
